import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ChevronDown, ChevronRight, ListChecks } from 'lucide-react';
import { toast } from 'sonner';

// İŞ TAKİBİ (kullanıcı, 2026-11-03): birkaç gün süren işlerin özeti. Özet
// sunucuda raporlardan türetilir (`GET /insaat/isler`); bir iş = aynı
// `is_id` taşıyan günlük rapor satırları. Ofis, sahada "tamamlandı"
// işaretlenmeyi unutup her gün forma gelen işi buradan kapatabilir.

const sayi = (n, k = 2) => Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: k });
const tarih = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('tr-TR') : '—');
const tutarMetni = (t) => Object.entries(t || {}).map(([pb, v]) => `${sayi(v)} ${pb}`).join(' + ') || '—';

export default function IsTakibi() {
  const navigate = useNavigate();
  const [isler, setIsler] = useState([]);
  const [sites, setSites] = useState([]);
  const [subs, setSubs] = useState([]);
  const [siteFilter, setSiteFilter] = useState('');
  const [subFilter, setSubFilter] = useState('');
  const [durum, setDurum] = useState('');
  const [acik, setAcik] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);

  useEffect(() => {
    api.get('/insaat/sites').then((r) => setSites(r.data)).catch(() => {});
    api.get('/insaat/subcontractors').then((r) => setSubs(r.data)).catch(() => {});
  }, []);

  const yukle = async () => {
    const params = {};
    if (siteFilter) params.site_id = siteFilter;
    if (subFilter) params.subcontractor_id = subFilter;
    if (durum) params.durum = durum;
    try {
      const r = await api.get('/insaat/isler', { params });
      setIsler(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşler yüklenemedi');
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { yukle(); }, [siteFilter, subFilter, durum]);

  const durumDegistir = async (o, tamamlandi) => {
    if (!window.confirm(tamamlandi
      ? `"${o.is_cinsi}" işi ${tarih(o.son_tarih)} tarihli son günüyle kapatılsın mı? Artık alt yüklenicinin formuna gelmez.`
      : `"${o.is_cinsi}" işi yeniden açılsın mı? Alt yüklenicinin bir sonraki raporuna devam eden iş olarak gelir.`)) return;
    setYukleniyor(true);
    try {
      await api.post(`/insaat/isler/${o.is_id}/durum`, { tamamlandi });
      toast.success(tamamlandi ? 'İş kapatıldı' : 'İş yeniden açıldı');
      await yukle();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydedilemedi');
    }
    setYukleniyor(false);
  };

  const siteName = (id) => sites.find((s) => s.id === id)?.name || '-';
  const subName = (id) => subs.find((s) => s.id === id)?.name || '-';
  const filtreSirasi = subs.filter((s) => !s.parent_id)
    .flatMap((s) => [s, ...subs.filter((a) => a.parent_id === s.id)]);
  const devamSayisi = isler.filter((o) => o.durum === 'devam').length;

  return (
    <div data-testid="insaat-is-takibi-page">
      <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight mb-1">İş Takibi</h1>
      <p className="text-sm text-[#8a7e70] mb-6">
        Birden fazla gün süren işler. Alt yüklenici "tamamlandı" işaretleyene kadar iş her gün raporuna gelir;
        toplam gün, adam·gün ve adam·saat raporlardan hesaplanır.
      </p>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="space-y-1">
          <label className="text-xs text-[#8a7e70] uppercase font-semibold">Şantiye</label>
          <Select value={siteFilter} onValueChange={(v) => setSiteFilter(v === '_all' ? '' : v)}>
            <SelectTrigger className="bg-white border-[#d0c4b0] w-44"><SelectValue placeholder="Tümü" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Tümü</SelectItem>
              {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-[#8a7e70] uppercase font-semibold">Alt Yüklenici</label>
          <Select value={subFilter} onValueChange={(v) => setSubFilter(v === '_all' ? '' : v)}>
            <SelectTrigger className="bg-white border-[#d0c4b0] w-44"><SelectValue placeholder="Tümü" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Tümü</SelectItem>
              {filtreSirasi.map((s) => (
                <SelectItem key={s.id} value={s.id} className={s.parent_id ? 'pl-8' : ''}>
                  {s.parent_id ? `↳ ${s.name}` : s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-[#8a7e70] uppercase font-semibold">Durum</label>
          <Select value={durum} onValueChange={(v) => setDurum(v === '_all' ? '' : v)}>
            <SelectTrigger className="bg-white border-[#d0c4b0] w-44"><SelectValue placeholder="Tümü" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Tümü</SelectItem>
              <SelectItem value="devam">Devam eden</SelectItem>
              <SelectItem value="tamamlandi">Tamamlanan</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-sm text-[#3F3E39] ml-auto">{isler.length} iş · {devamSayisi} devam ediyor</p>
      </div>

      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              <TableHead className="w-8" />
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">İş</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Alt Yüklenici / Yer</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Başlangıç</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Bitiş</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Gün</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Adam·gün</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Adam·saat</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Miktar</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Tutar</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isler.map((o) => (
              <Fragment key={o.is_id}>
                <TableRow className="border-[#d0c4b0] cursor-pointer hover:bg-[#f5f0e8]"
                  onClick={() => setAcik(acik === o.is_id ? null : o.is_id)} data-testid={`is-satir-${o.is_id}`}>
                  <TableCell className="text-[#8a7e70]">{acik === o.is_id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</TableCell>
                  <TableCell className="max-w-sm">
                    <div className="flex items-start gap-2">
                      <ListChecks size={14} className="text-[#8a7e70] mt-1 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[#1a1916] font-medium break-words">
                          {o.poz_no && <span className="font-mono text-sm text-[#5c554b] mr-1.5">{o.poz_no}</span>}
                          {o.is_cinsi}
                          {o.tur === 'ilave' && <span className="ml-1.5 status-badge bg-[#7a6a2e]/10 text-[#7a6a2e]">İlave</span>}
                        </p>
                        {o.masraf_grubu_yolu && <p className="text-xs text-[#8a7e70]">{o.masraf_grubu_yolu}</p>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-[#3F3E39]">
                    {subName(o.subcontractor_id)}
                    <p className="text-xs text-[#8a7e70]">{siteName(o.site_id)}{o.konum_yolu && o.konum_yolu !== 'Şantiye geneli' ? ` · ${o.konum_yolu}` : ''}</p>
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{tarih(o.baslangic)}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">
                    {o.durum === 'tamamlandi'
                      ? tarih(o.bitis)
                      : <span className="status-badge bg-[#2e6fa0]/10 text-[#24557a]">Devam ediyor</span>}
                  </TableCell>
                  <TableCell className="text-right font-mono">{o.gun}</TableCell>
                  <TableCell className="text-right font-mono">{sayi(o.adam_gun)}</TableCell>
                  <TableCell className="text-right font-mono">{sayi(o.adam_saat)}</TableCell>
                  <TableCell className="text-right font-mono whitespace-nowrap">
                    {o.miktar > 0 ? `${sayi(o.miktar, 3)} ${o.birim || ''}` : '—'}
                  </TableCell>
                  <TableCell className="text-right font-mono whitespace-nowrap">{tutarMetni(o.tutarlar)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="outline" disabled={yukleniyor} data-testid={`is-durum-${o.is_id}`}
                      onClick={() => durumDegistir(o, o.durum !== 'tamamlandi')}
                      className={`h-8 whitespace-nowrap ${o.durum === 'tamamlandi' ? 'border-[#d0c4b0]' : 'border-[#3d6e4e]/50 text-[#3d6e4e]'}`}>
                      {o.durum === 'tamamlandi' ? 'Yeniden aç' : 'Kapat'}
                    </Button>
                  </TableCell>
                </TableRow>
                {acik === o.is_id && (
                  <TableRow className="bg-white hover:bg-white border-[#d0c4b0]">
                    <TableCell />
                    <TableCell colSpan={10} className="py-3">
                      <p className="text-xs font-semibold uppercase text-[#8a7e70] mb-2">Gün gün</p>
                      <table className="text-sm">
                        <tbody>
                          {o.gunler.map((g) => (
                            <tr key={g.line_id} className="border-b border-[#efe8dc] last:border-0">
                              <td className="pr-4 py-1">
                                <button type="button" className="underline text-[#2e6fa0]"
                                  onClick={() => navigate(`/erp/insaat/reports/${g.report_id}`)}>{tarih(g.tarih)}</button>
                              </td>
                              <td className="pr-4 py-1 text-[#3F3E39]">
                                {g.kisi > 0 ? `${g.kisi} kişi × ${sayi(g.saat)} saat` : 'Ekip yok'}
                                {g.ekip.length > 0 && <span className="text-[#8a7e70]"> ({g.ekip.map((e) => `${e.adet} ${e.pozisyon}`).join(', ')})</span>}
                              </td>
                              <td className="pr-4 py-1 font-mono">{g.miktar > 0 ? `${sayi(g.miktar, 3)} ${o.birim || ''}` : ''}</td>
                              <td className="py-1">{g.tamamlandi && <span className="status-badge bg-[#3d6e4e]/10 text-[#3d6e4e]">Tamamlandı</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
            {isler.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-8 text-[#8a7e70]">
                  Kayıt yok. İşler, alt yüklenici günlük raporu gönderdikçe burada görünür.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
